import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom'; // Importar useNavigate
import * as authService from '../services/authService'; // Importar TUDO do serviço
import { User, LoginData, AuthResponse } from '../services/authService'; // Importar tipos

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  permissions: Record<string, boolean>;
  isLoading: boolean;
  login: (data: LoginData) => Promise<void>; // Modificado para não retornar AuthResponse diretamente
  logout: () => void;
  hasPermission: (permissionKey: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Começa carregando para verificar token
  const navigate = useNavigate(); // Obter a função navigate

  // Função interna para buscar permissões (similar ao serviço, mas atualiza estado)
  const fetchAndSetPermissions = async (): Promise<Record<string, boolean>> => {
    try {
      // Reutiliza a lógica de chamada API do serviço, se aplicável,
      // ou faz a chamada aqui mesmo.
      const response = await axios.get<Record<string, boolean>>('/api/auth/permissions');
      const fetchedPermissions = response.data || {};
      setPermissions(fetchedPermissions);
      return fetchedPermissions;
    } catch (error) {
      console.error('[AuthProvider] Erro ao buscar permissões:', error);
      handleLogout(); // Desloga se não conseguir buscar permissões
      return {};
    }
  };
  
  // Verificar token ao montar o provider
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      const token = authService.getToken();
      if (token) {
        try {
            // Idealmente, teríamos um endpoint /api/auth/me para validar token
            // e retornar user + permissions de uma vez.
            // Por enquanto, buscamos user do localStorage e permissions da API.
            const storedUser = authService.getCurrentUser();
            if (storedUser) {
                setUser(storedUser);
                await fetchAndSetPermissions(); // Busca permissões
                setIsAuthenticated(true);
            } else {
                // Token existe mas usuário não está no localStorage? Limpar.
                handleLogout();
            }
        } catch (error) {
          console.error("[AuthProvider] Erro ao validar token:", error);
          handleLogout(); // Limpa se o token for inválido/expirado
        }
      } else {
          setIsAuthenticated(false);
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  const handleLogin = async (data: LoginData) => {
    setIsLoading(true);
    try {
      // 1. Chama o serviço de login SÓ para obter token e dados básicos
      const response = await authService.login(data); 
      // O serviço login AGORA NÃO busca mais permissões nem usa timeout.
      // Ele apenas faz o POST, salva token/user e retorna.
      
      // 2. Atualiza o estado do usuário no contexto
      setUser(response.user);
      setIsAuthenticated(true);
      
      // 3. Busca e atualiza as permissões no estado do contexto
      await fetchAndSetPermissions(); 
    } catch (error) {
      console.error("[AuthProvider] Falha no login:", error);
      handleLogout(); // Limpa o estado em caso de falha
      throw error; // Re-lança o erro para a página de login tratar (ex: mostrar msg)
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    authService.logout(); // Limpa localStorage via serviço
    setUser(null);
    setPermissions({});
    setIsAuthenticated(false); // Atualiza o estado
    navigate('/login', { replace: true }); 
  };

  const checkPermission = (permissionKey: string): boolean => {
    return permissions[permissionKey] === true;
  };

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        user, 
        permissions, 
        isLoading, 
        login: handleLogin, 
        logout: handleLogout, 
        hasPermission: checkPermission 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook customizado para usar o contexto
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}; 